import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import MenuLine from './MenuLine';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare } from '@fortawesome/pro-duotone-svg-icons';
import { useStore } from '../../store/store';
import { isVariableCustom } from '../../utils/variables';
import { getAnchorOffset, injectVariableInEditorState } from '../RichEditor/utils';
import { Input, Switch, message } from 'antd';
import AddVariableForm from './AddVariableForm';
import { mutateInsertVariableInOrderArray } from '../../utils/variableOrderArray';
import { useTranslation } from 'react-i18next';

const createVariableKey = "createVariable"

const delimiter = "@"

const selector = ({
  variables,
  currentDocument,
  updateVariable,
  currentTemplate,
  recursiveModelLabels,
}) => ({
  variables,
  isTemplate: currentDocument.type === 'template',
  isOfferProposal: currentDocument.type === 'offer',
  updateVariable,
  currentTemplate,
  recursiveModelLabels,
});

const SelectionVariableTranslator = ({
  options,
  selectedOptionKey,
  setSelectedOptionKey,
  setEditorState,
  startOffset,
  endOffset,
  blockText,
  setOptions,
  setVisibleOptions,
}) => {
  const {
    variables,
    isTemplate,
    isOfferProposal,
    updateVariable,
    currentTemplate,
    recursiveModelLabels,
  } = useStore(selector)
  const { t } = useTranslation();

  const capitalize = (str) => {
    if (!str || typeof str !== 'string') return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const extractGroupLabel = (translatedName) => {
    if (!translatedName || typeof translatedName !== 'string') return translatedName;

    // On retire le préfixe \"Nom de ...\" pour garder uniquement le type d'entité
    let label = translatedName.replace(/^Nom de l'|^Nom de la |^Nom du |^Nom de /i, '').trim();

    // Si on a encore quelque chose, on capitalise la première lettre
    if (label.length > 0) {
      label = capitalize(label);
    }
    return label;
  };
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const [showOnlyRecursive, setShowOnlyRecursive] = useState(false);

  const isRecursiveTemplate = isTemplate && currentTemplate?.isRecursive;

  const variableInsertionRef = useRef(null);


  const handleSwitchChange = (checked) => {
    setShowCustom(checked);
  };

  // Libellé affiché d'une variable récursive, ex. « Quantité (Article) » : la
  // recherche porte dessus, sinon taper le nom du modèle ne trouve rien.
  const recursiveOptionLabel = useCallback((variableKey, name) => {
    const modelName = variableKey.split('.')[0];
    const modelLabel = recursiveModelLabels?.[modelName] ?? t(`recursive_sections.model_labels.${modelName}`, { defaultValue: modelName });
    const modelPreposition = t(`recursive_sections.model_label_prepositions.${modelName}`, { defaultValue: `du ${modelLabel.toLowerCase()}` });
    return t('recursive_sections.field_label_pattern', {
      fieldLabel: name,
      modelLabel: modelLabel.toLowerCase(),
      modelPreposition,
      defaultValue: `${name} (${modelLabel})`,
    });
  }, [recursiveModelLabels, t]);

  const formatVariablesAsOptions = useCallback(() => {
    if (!variables || endOffset !== startOffset) {
      return [];
    }

    let startVariableName;
    for (
      startVariableName = startOffset - 1;
      startVariableName > 0 && blockText[startVariableName] !== delimiter;
      startVariableName -= 1);
    if (blockText[startVariableName] !== delimiter ||
      (startVariableName !== 0 && blockText[startVariableName - 1].match(/^[0-9a-z]+$/i))) {
      return []
    }

    const variableSearchInput = blockText.slice(startVariableName + 1, startOffset);

    // Normalisation + recherche par mots (tous les mots doivent être trouvés)
    const normalizedSearchInput = variableSearchInput
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim();

    const searchTokens = normalizedSearchInput
      ? normalizedSearchInput.split(/\s+/).filter(Boolean)
      : [];

    const variableOptions = Object.entries(variables)
      .filter(([key, variable]) => {
        if (variable.isImg) return false;

        // Si aucun mot saisi après '@', on ne filtre pas (toutes les variables)
        if (searchTokens.length === 0) return true;

        const label = variable.isRecursive
          ? recursiveOptionLabel(key, variable.name || '')
          : variable.name;
        const normalizedName = (label || '')
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "");

        // Tous les mots tapés doivent être présents quelque part dans le nom
        return searchTokens.every((token) => normalizedName.includes(token));
      })
      .map(([key, variable]) => ({ key, name: variable.name, isRecursive: !!variable.isRecursive }))

    if (!isTemplate) {
      return variableOptions
    }

    return [
      { key: createVariableKey, name: "Créer une variable", isRecursive: false },
      ...variableOptions,
    ]
  }, [blockText, endOffset, isTemplate, startOffset, variables, recursiveOptionLabel])

  useEffect(() => {
    const newOptions = formatVariablesAsOptions()
    setOptions((prev) => {
      if (isPopoverOpen && newOptions.length === 0 && prev.length > 0) {
        return prev
      }
      return newOptions
    })
  }, [formatVariablesAsOptions, setOptions, isPopoverOpen])

  const constructOptionName = (variableKey, name, isRecursive = false) => {
    if (isRecursive) {
      return recursiveOptionLabel(variableKey, name);
    }
    let baseName = "";
    if (variableKey === createVariableKey) {
      baseName = <FontAwesomeIcon
        icon={faPenToSquare}
        style={{ marginRight: 6 }}
      />
    }
    if (isOfferProposal && !isVariableCustom(variableKey) && variableKey !== createVariableKey) {
      const isOpportunityVariable = variableKey.startsWith("D-");
      if (isOpportunityVariable) {
        // Variable provenant d'une opportunité liée à l'offre
        const parts = variableKey.split(".");
        const docId = parts[0];
        const groupKey = parts[1];

        // Tenter de récupérer la vraie valeur de nom d'opportunité :
        // variable clé du type "<docId>.opportunity.NAME"
        const opportunityNameKey = `${docId}.opportunity.NAME`;
        const opportunityVariable = variables[opportunityNameKey];
        const realOpportunityName =
          opportunityVariable?.value || opportunityVariable?.name;

        const opportunityLabel =
          realOpportunityName && typeof realOpportunityName === 'string'
            ? realOpportunityName
            : t('opportunity.NAME'); // fallback sur le libellé \"Nom de l'opportunité\"

        // On essaie de récupérer un label de groupe à partir de la traduction \"<group>.NAME\"
        const rawGroupName = t(`${groupKey}.NAME`);
        const groupLabel =
          rawGroupName && !rawGroupName.startsWith(`${groupKey}.`)
            ? extractGroupLabel(rawGroupName)
            : capitalize(groupKey);

        baseName = `${opportunityLabel} / ${groupLabel} / `;
      } else {
        // Variable propre à l'offre courante
        // On affiche simplement \"Offre\" (clé i18n 'offer') au lieu de \"Variables de l'offre\"
        baseName = `${t('offer')} / `;
      }
    }
    return <>{baseName}{name}</>
  }

  const handleUpdateVariable = useCallback((variableKey) => {
    updateVariable(variableKey, (variable, draft) => {
      if (variable.notUsed) {
        delete variable.notUsed
        mutateInsertVariableInOrderArray(draft, variableKey)
      }
    })
  }, [updateVariable])

  const rememberVariableInsertionPoint = useCallback(() => {
    setEditorState((editorState) => {
      const selection = editorState.getSelection();
      const insertionStartOffset = selection.getStartOffset();
      variableInsertionRef.current = {
        blockKey: selection.getStartKey(),
        startOffset: insertionStartOffset,
        anchorOffset: getAnchorOffset({
          editorState,
          startOffset: insertionStartOffset,
          delimiter,
        }),
      };
      return editorState;
    });
  }, [setEditorState]);

  const injectVariableInText = useCallback((
    variableKey,
    variablesInStore) => {
    setIsPopoverOpen(false);
    setEditorState((editorState) => {
      const insertion = variableInsertionRef.current;
      variableInsertionRef.current = null;
      const insertionStartOffset = insertion?.startOffset ?? startOffset;
      const blockKey =
        insertion?.blockKey ?? editorState.getSelection().getStartKey();
      const anchorOffset =
        insertion?.anchorOffset ??
        getAnchorOffset({ editorState, startOffset: insertionStartOffset, delimiter });

      return injectVariableInEditorState({
        editorState,
        variableKey,
        variablesInStore,
        isTemplate,
        startOffset: insertionStartOffset,
        anchorOffset,
        blockKey,
      })
    })
  }, [isTemplate, setEditorState, startOffset])

  const onSelect = useCallback((event, variableKey) => {
    event.stopPropagation()
    event.preventDefault()
    if (variableKey === createVariableKey) {
      rememberVariableInsertionPoint()
      setIsPopoverOpen(true)
    } else {
      // Collab : insertion refusée si la variable est éditée par un co-éditeur
      const { boxLocks, user } = useStore.getState();
      const lock = boxLocks[variableKey];
      if (lock && lock.id !== user?.id) {
        message.warning(`Variable en cours d'édition par ${lock.name}`);
        return;
      }
      injectVariableInText(variableKey, variables)
      if (variables[variableKey]) {
        handleUpdateVariable(variableKey)
      }
    }
  }, [injectVariableInText, handleUpdateVariable, variables, variables])

  const handleSelectAction = useCallback((event) => {
    if (!['Enter'].includes(event.key) || isPopoverOpen || !options || options?.length < 1 || !selectedOptionKey) return;

    onSelect(event, selectedOptionKey)
  }, [onSelect, isPopoverOpen, options, selectedOptionKey])

  const handleSearch = (value) => {
    setSearchTerm(value.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, ""));
  };

  useEffect(() => {
    window.addEventListener('keydown', handleSelectAction, true);
    return () => {
      window.removeEventListener('keydown', handleSelectAction, true);
    };
  }, [handleSelectAction]);

  const renderLine = (key, name, customButtonStyle = {}, customClassName = '', isRecursive = false) =>
    <MenuLine
      key={key}
      optionKey={key}
      optionName={constructOptionName(key, name, isRecursive)}
      selectedOptionKey={selectedOptionKey}
      onSelect={onSelect}
      setSelectedOptionKey={setSelectedOptionKey}
      customButtonStyle={customButtonStyle}
      customClassName={customClassName}
      isRecursive={isRecursive}
    />

  const displayedOptions = useMemo(() => options.filter(({ key, name, isRecursive: optIsRecursive }) => {
    if (key === "createVariable") return true;

    const isCustom = key.startsWith("custom.");

    const normalizedSearchTerm = searchTerm.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const label = optIsRecursive ? recursiveOptionLabel(key, name) : name;
    const normalizedName = label.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

    if (showOnlyRecursive && isRecursiveTemplate) {
      return !!optIsRecursive && normalizedName.includes(normalizedSearchTerm);
    }

    if (showCustom) {
      return isCustom && normalizedName.includes(normalizedSearchTerm);
    }

    return normalizedName.includes(normalizedSearchTerm);
  })
    .sort((a, b) => {
      // Toujours afficher "Créer une variable" en premier
      if (a.key === createVariableKey) return -1;
      if (b.key === createVariableKey) return 1;

      // Nouveau tri spécifique pour le mode offre :
      // 1. Variables de l'offre
      // 2. Variables d'opportunités (classées par numéro d'opportunité = partie docId du key)
      // 3. Le reste (head_office, entity, template, custom, etc.)
      if (isOfferProposal) {
        const isOfferVar = (key) =>
          !key.startsWith('D-') && !key.startsWith('custom.');
        const isOpportunityVar = (key) => key.startsWith('D-');

        const category = (key) => {
          if (isOfferVar(key)) return 1;
          if (isOpportunityVar(key)) return 2;
          return 3;
        };

        const catA = category(a.key);
        const catB = category(b.key);
        if (catA !== catB) return catA - catB;

        // Pour les variables d'opportunité :
        //  - d'abord par identifiant d'opportunité (docId)
        //  - puis par groupe avec un ordre figé
        //    Opportunité > Agence concernée > Entreprise facturée > Entreprise prestée > Entreprise décisionnaire > Utilisateur
        //  - puis par nom de variable (clé tertiaire), en dernier recours par libellé
        if (catA === 2) {
          const partsA = a.key.split('.');
          const partsB = b.key.split('.');
          const [docIdA, groupA = '', subKeyA = ''] = partsA;
          const [docIdB, groupB = '', subKeyB = ''] = partsB;

          if (docIdA !== docIdB) {
            return docIdA.localeCompare(docIdB);
          }
          if (groupA !== groupB) {
            const groupOrder = {
              opportunity: 1,
              entity: 2,
              billed_customer: 3,
              provided_customer: 4,
              decision_maker_customer: 5,
              user: 6,
            };
            const orderA = groupOrder[groupA] || 100;
            const orderB = groupOrder[groupB] || 100;
            if (orderA !== orderB) return orderA - orderB;
            // Si les deux groupes sont "autres", on les trie entre eux par ordre alpha
            if (orderA === 100 && orderB === 100) {
              return groupA.localeCompare(groupB);
            }
          }
          if (subKeyA !== subKeyB) {
            return subKeyA.localeCompare(subKeyB);
          }
        }
      }

      const isCustomA = a.key.startsWith("custom.");
      const isCustomB = b.key.startsWith("custom.");

      if (isCustomA && !isCustomB) return 1;
      if (!isCustomA && isCustomB) return -1;

      return a.name.localeCompare(b.name);
    }), [options, searchTerm, showOnlyRecursive, isRecursiveTemplate, showCustom, isOfferProposal, recursiveOptionLabel]);

  // Flèches et Entrée suivent la liste affichée, pas `options` brute.
  useLayoutEffect(() => {
    setVisibleOptions(displayedOptions);
    return () => setVisibleOptions(null);
  }, [displayedOptions, setVisibleOptions]);

  return (
    <div style={{ width: 500 }}>
      <div
        style={{ display: "flex", flexDirection: "row", padding: 10 }}
        onMouseDown={(event) => {
          event.stopPropagation();
        }}
        onClick={(event) => {
          event.stopPropagation();
        }}
        onKeyDown={(event) => {
          event.stopPropagation();
        }}
      >
        <Input.Search
          placeholder="Rechercher une variable"
          onSearch={handleSearch}
          onChange={(e) => handleSearch(e.target.value)}
          style={{ marginBottom: 8 }}
        />
        {isRecursiveTemplate && (
          <Switch
            style={{ marginLeft: 8, minWidth: 100 }}
            checked={showOnlyRecursive}
            onChange={setShowOnlyRecursive}
            checkedChildren="Récursive"
            unCheckedChildren="Toutes"
          />
        )}
        {/* <Switch style={{ width: 160, margin: 5 }} checked={showCustom} onChange={handleSwitchChange} checkedChildren="Personnalisées" unCheckedChildren="Variables" /> */}
      </div>
      {displayedOptions
        .map(({ key, name, isRecursive: optIsRecursive }) =>
          key === createVariableKey ? (
            <React.Fragment key={key}>
              {renderLine(
                key,
                name,
                { fontWeight: 'bold' },
                'create'
              )}
              {isPopoverOpen && (
                <li
                  className="variable-create-form-panel"
                  onMouseDown={(event) => event.stopPropagation()}
                  onClick={(event) => event.stopPropagation()}
                >
                  <div className="variable-create-form-panel__title">
                    Créer une nouvelle variable
                  </div>
                  <AddVariableForm
                    setIsPopoverOpen={setIsPopoverOpen}
                    filteredOptions={displayedOptions}
                    injectVariableInText={injectVariableInText}
                  />
                </li>
              )}
            </React.Fragment>
          ) : (
            renderLine(key, name, {}, '', optIsRecursive)
          )
        )}
    </div>
  );
}

export default SelectionVariableTranslator;
